// Phase 24B — Generic transaction report page (used for order/visit/working/new-call/sales-return).
// The differences are: slug, page title, filter set, default date range.
import { todayIST } from "@/lib/istDate";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RefreshCcw, Loader2, Info } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import CommonFilterBar from "@/components/reports/CommonFilterBar";
import PresetsMenu from "@/components/reports/PresetsMenu";
import ExportButtons from "@/components/reports/ExportButtons";
import ColumnVisibilityMenu from "@/components/reports/ColumnVisibilityMenu";
import { useReportsBackLink } from "@/components/reports/useReportsBackLink";
import PageHeader from "@/components/PageHeader";

export default function TransactionReportPage({
  slug, title, subtitle, initialDaysBack = 30, filterConfig = {},
  testidPrefix, extraChip = null, headerWidget = null,
}) {
  // Every report opens on TODAY; pick another date range to see more.
  const today = useMemo(() => todayIST(), []);
  const startDate = today;
  const [filters, setFilters] = useState({
    date_from: startDate, date_to: today,
    zone_id: "", sub_zone_id: "", user_id: "",
  });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(1);
  const [visible, setVisible] = useState(null); // null = all
  const reportsBackTo = useReportsBackLink();

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = { page, page_size: 50 };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data: d } = await api.get(`/reports/${slug}`, { params });
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }, [filters, page, slug]);

  useEffect(() => { load(); }, [load]);

  const columns = data?.columns || [];
  const items = data?.items || [];
  const total = data?.total || 0;

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid={`${testidPrefix}-page`}>
      <PageHeader
        breadcrumbs={[{ label: "Reports", to: reportsBackTo }, { label: title || "Transactions" }]}
        title={title || "Transaction report"}
        subtitle={subtitle || "Line-level transaction log with GL bucket and audit-trail links."}
        favorite={{ key: `reports.${slug}`, type: "report", label: title || "Transaction report", route: `/reports/${slug}` }}
        testid={`${testidPrefix}-page-header`}
        actions={extraChip}
      />

      {/* flex-wrap directly on the button row (not just the outer wrapper) —
          otherwise this row only breaks onto a new line as a whole, and can
          still overflow the ~480px field/manager shell horizontally when
          opened in a wide desktop browser (see CommonFilterBar for why). */}
      <div className="flex items-start justify-end flex-wrap gap-2">
        <PresetsMenu reportKey={slug} filters={filters}
                        onApply={(f) => setFilters({ ...filters, ...f })} />
        {columns.length > 0 && (
          <ColumnVisibilityMenu reportKey={slug} columns={columns} onChange={setVisible} />
        )}
        <ExportButtons endpoint={`/reports/${slug}`} filename={slug}
                       params={visible && visible.length !== columns.length
                                 ? { ...filters, visible_columns: visible.join(",") }
                                 : filters} />
        <Button variant="outline" size="sm" onClick={load} disabled={busy}
                  data-testid={`${testidPrefix}-refresh`}>
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-1" />}
          Refresh
        </Button>
      </div>

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-3">
          <CommonFilterBar value={filters} onChange={setFilters}
                              showZone showSubZone showDateRange
                              {...filterConfig} />
        </CardContent>
      </Card>

      {headerWidget && headerWidget(filters)}

      {data?.filter_summary && (
        <div className="text-xs text-neutral-500 flex items-center gap-1">
          <Info className="w-3 h-3" /> {data.filter_summary} · {total} rows
        </div>
      )}

      {data?.summary && typeof data.summary.net_kg === "number" && (
        <div className="flex flex-wrap gap-3" data-testid={`${testidPrefix}-kg-summary`}>
          <div className="bg-white border rounded-lg px-3 py-2 min-w-[120px]">
            <div className="text-[11px] text-neutral-500">Orders (kg)</div>
            <div className="text-lg font-bold">{data.summary.orders_kg.toLocaleString("en-IN")}</div>
          </div>
          <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 min-w-[120px]">
            <div className="text-[11px] text-rose-700">Returns (kg)</div>
            <div className="text-lg font-bold text-rose-800">−{data.summary.returns_kg.toLocaleString("en-IN")}</div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 min-w-[120px]">
            <div className="text-[11px] text-emerald-700">Net (kg)</div>
            <div className="text-lg font-bold text-emerald-800">{data.summary.net_kg.toLocaleString("en-IN")}</div>
          </div>
        </div>
      )}

      <Card className="border-neutral-200 rounded-2xl overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto max-h-[600px]">
            <table className="w-full text-xs" data-testid={`${testidPrefix}-table`}>
              <thead className="bg-[#6D2C91] text-white sticky top-0">
                <tr>
                  {columns.filter(c => !visible || visible.includes(c.key)).map(c => (
                    <th key={c.key} className="text-left px-3 py-2 font-semibold whitespace-nowrap">
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={(visible?.length || columns.length) || 1}
                            className="text-center p-6 text-neutral-500">
                    No rows match the current filters.
                  </td></tr>
                ) : items.map((row, i) => (
                  <tr key={i} className="border-t hover:bg-neutral-50">
                    {columns.filter(c => !visible || visible.includes(c.key)).map(c => (
                      <td key={c.key} className="px-3 py-2 whitespace-nowrap"
                          data-col={c.key}>
                        {renderCell(row[c.key], c)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {total > 50 && (
        <div className="flex items-center justify-between text-xs text-neutral-500">
          <span>Page {page} of {Math.ceil(total / 50)} · {total} total rows</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 1}
                      onClick={() => setPage(p => p - 1)}>Prev</Button>
            <Button size="sm" variant="outline" disabled={page * 50 >= total}
                      onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}


function renderCell(v, col) {
  if (v == null || v === "") return "—";
  if (col.format === "boolean") return v ? "Yes" : "No";
  if (col.format === "currency") {
    const n = Number(v);
    if (!isFinite(n)) return v;
    return "₹" + n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
  }
  if (col.format === "number") {
    const n = Number(v);
    if (!isFinite(n)) return v;
    return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
  }
  if (col.format === "gps") {
    if (typeof v === "number") return v.toFixed(5);
    return v;
  }
  if (col.format === "duration_sec") {
    const sec = Number(v);
    if (!isFinite(sec)) return v;
    return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
  }
  return String(v);
}
